import React from 'react';
import { Link } from 'react-router-dom';
import TopAppBar from '../components/TopAppBar';
import BottomNavBar from '../components/BottomNavBar';
import Footer from '../components/Footer';

const AboutPage: React.FC = () => {
  return (
    <div className="bg-background text-on-background min-h-screen pb-24 md:pb-0">
      <TopAppBar />

      <main className="max-w-3xl mx-auto px-container-margin py-10 md:py-16 space-y-12">

        {/* Breadcrumb */}
        <div className="flex items-center gap-3">
          <Link to="/" className="text-primary hover:underline text-sm">Home</Link>
          <span className="text-outline">/</span>
          <h1 className="text-3xl font-bold text-on-surface">About us</h1>
        </div>

        {/* Mission */}
        <div className="bg-surface border border-outline-variant rounded-2xl p-6 md:p-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="material-symbols-outlined text-primary text-3xl" style={{ fontVariationSettings: "'FILL' 1" }}>
              construction
            </span>
            <h2 className="text-xl font-bold text-on-surface">Our mission</h2>
          </div>
          <p className="text-on-surface-variant leading-relaxed">
            Buildon connects homeowners and property developers with skilled, vetted contractors across South Africa.
            We believe finding a reliable builder, electrician, or plumber should be straightforward — no guesswork, no cold calls, no regret.
          </p>
          <p className="text-on-surface-variant leading-relaxed mt-3">
            Every contractor on our platform goes through a compliance and credentials review before they can be verified.
            That means you see real credentials, real reviews, and real accountability.
          </p>
        </div>

        {/* How it works */}
        <div>
          <h2 className="text-xl font-bold text-on-surface mb-5">How it works</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              {
                icon: 'search',
                title: 'Find a contractor',
                body: 'Browse by category or service area. Filter by rating, tier, and verified status to narrow down the right fit.',
              },
              {
                icon: 'verified',
                title: 'Check credentials',
                body: "View a contractor's compliance documents, registration numbers, and client reviews — all in one place.",
              },
              {
                icon: 'handshake',
                title: 'Get the job done',
                body: 'Contact the contractor directly through the platform. Post a job and let contractors come to you.',
              },
            ].map(item => (
              <div key={item.title} className="bg-surface border border-outline-variant rounded-2xl p-5">
                <span className="material-symbols-outlined text-primary text-2xl mb-3 block" style={{ fontVariationSettings: "'FILL' 1" }}>
                  {item.icon}
                </span>
                <h3 className="font-semibold text-on-surface mb-1">{item.title}</h3>
                <p className="text-sm text-on-surface-variant leading-relaxed">{item.body}</p>
              </div>
            ))}
          </div>
        </div>

        {/* For contractors */}
        <div className="bg-primary/5 border border-primary/20 rounded-2xl p-6 md:p-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="material-symbols-outlined text-primary text-2xl" style={{ fontVariationSettings: "'FILL' 1" }}>
              business_center
            </span>
            <h2 className="text-xl font-bold text-on-surface">For contractors</h2>
          </div>
          <p className="text-on-surface-variant leading-relaxed mb-4">
            Buildon is built for professionals who take their work seriously. Create a profile, upload your compliance documents,
            and get discovered by clients who are actively looking for your skills.
          </p>
          <ul className="space-y-2 text-sm text-on-surface-variant">
            {[
              'Free to list your services',
              'Verified badge builds client trust',
              'Tiered profiles for premium visibility',
              'Direct job leads from property owners',
              "Already know your client? Send them a quote directly by email - even if they haven't signed up yet",
            ].map(point => (
              <li key={point} className="flex items-center gap-2">
                <span className="material-symbols-outlined text-primary text-[16px]" style={{ fontVariationSettings: "'FILL' 1" }}>check_circle</span>
                {point}
              </li>
            ))}
          </ul>
          <div className="mt-5">
            <Link
              to="/register"
              className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
            >
              Register as a contractor
              <span className="material-symbols-outlined text-[16px]">arrow_forward</span>
            </Link>
          </div>
        </div>

        {/* Values */}
        <div>
          <h2 className="text-xl font-bold text-on-surface mb-5">What we stand for</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { icon: 'security', title: 'Trust', body: 'Credentials are reviewed by our moderation team. Verified means verified.' },
              { icon: 'star', title: 'Quality', body: 'Client reviews and ratings keep standards high and contractors accountable.' },
              { icon: 'location_on', title: 'Local', body: 'We focus on South African contractors, service areas, and regulations.' },
              { icon: 'support_agent', title: 'Support', body: 'Questions or disputes? Our team is available to help both sides reach a fair outcome.' },
            ].map(item => (
              <div key={item.title} className="flex gap-4 bg-surface border border-outline-variant rounded-2xl p-5">
                <span className="material-symbols-outlined text-primary text-2xl flex-shrink-0 mt-0.5" style={{ fontVariationSettings: "'FILL' 1" }}>
                  {item.icon}
                </span>
                <div>
                  <h3 className="font-semibold text-on-surface mb-1">{item.title}</h3>
                  <p className="text-sm text-on-surface-variant leading-relaxed">{item.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center bg-surface border border-outline-variant rounded-2xl p-8">
          <h2 className="text-xl font-bold text-on-surface mb-2">Ready to get started?</h2>
          <p className="text-sm text-on-surface-variant mb-5">Find a vetted contractor or list your services today.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/contractors"
              className="px-6 py-2.5 bg-primary text-on-primary rounded-lg font-semibold text-sm hover:bg-primary/90 transition-colors"
            >
              Find a contractor
            </Link>
            <Link
              to="/contact"
              className="px-6 py-2.5 border border-outline-variant text-on-surface rounded-lg font-semibold text-sm hover:bg-surface-container transition-colors"
            >
              Get in touch
            </Link>
          </div>
        </div>

      </main>

      <Footer />
      <BottomNavBar />
    </div>
  );
};

export default AboutPage;
